const LAST_AUTH_STORAGE_KEY = 'boost:last-auth-info';

export const LAST_AUTH_METHOD = {
  EMAIL_PASSWORD: 'email_password',
  GOOGLE_OAUTH: 'google_oauth',
  LINKEDIN_OAUTH: 'linkedin_oauth',
  PASSKEY: 'passkey',
} as const;

export type LastAuthMethod = (typeof LAST_AUTH_METHOD)[keyof typeof LAST_AUTH_METHOD];

/** Social login providers — values match the backend `/api/auth/{provider}` Socialite driver names. */
export const AUTH_PROVIDER = {
  GOOGLE: 'google',
  LINKEDIN: 'linkedin',
} as const;

export type AuthProvider = (typeof AUTH_PROVIDER)[keyof typeof AUTH_PROVIDER];

/**
 * Social providers we currently offer on the login, registration and start page screens.
 * Every provider keeps its OAuth popup flow and backend endpoint regardless — this list
 * only decides which buttons are rendered, so hiding one is a matter of dropping it here.
 * LinkedIn is off for now.
 */
export const ENABLED_AUTH_PROVIDERS: AuthProvider[] = [AUTH_PROVIDER.GOOGLE];

/**
 * Match.Express turns LinkedIn back on: its attendees arrive FROM a LinkedIn
 * conversation about a company, so "the account you already use there" is the
 * primary way in, not an alternative. Boost.Express keeps the list above.
 */
export const MATCH_ENABLED_AUTH_PROVIDERS: AuthProvider[] = [
  AUTH_PROVIDER.LINKEDIN,
  AUTH_PROVIDER.GOOGLE,
];

/**
 * Whether the sign-in button of the given social provider should be rendered.
 *
 * @param provider the provider in question
 * @param providers the product's list; defaults to Boost.Express
 */
export const isAuthProviderEnabled = (
  provider: AuthProvider,
  providers: AuthProvider[] = ENABLED_AUTH_PROVIDERS,
): boolean => providers.includes(provider);

export interface LastAuthInfo {
  method: LastAuthMethod;
  email?: string;
  timestamp: number;
}

const getStorage = () => (typeof window === 'undefined' ? null : window.localStorage);

const parseStoredValue = (value: string | null): LastAuthInfo | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as LastAuthInfo;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('Failed to parse last auth info from localStorage', error);
    return null;
  }
};

export const getLastAuthInfo = (): LastAuthInfo | null => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(LAST_AUTH_STORAGE_KEY);
  return parseStoredValue(rawValue);
};

export const setLastAuthInfo = (info: LastAuthInfo): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  storage.setItem(LAST_AUTH_STORAGE_KEY, JSON.stringify(info));
};

const buildNextInfo = (payload: Partial<LastAuthInfo>): LastAuthInfo => {
  const current = getLastAuthInfo();
  const method = payload.method || current?.method || LAST_AUTH_METHOD.EMAIL_PASSWORD;

  return {
    method,
    email: payload.email ?? current?.email,
    timestamp: Date.now(),
  };
};

export const updateLastAuthInfo = (payload: Partial<LastAuthInfo>): LastAuthInfo => {
  const nextInfo = buildNextInfo(payload);
  setLastAuthInfo(nextInfo);
  return nextInfo;
};

export const clearLastAuthInfo = (): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(LAST_AUTH_STORAGE_KEY);
};

export const LAST_AUTH_INFO_STORAGE_KEY = LAST_AUTH_STORAGE_KEY;
